/**
 * @file        src/pages/wallet/WalletUsers.jsx
 * @description Wallet Management — search RETAIL (B2C) wallet holders and open
 *              one's wallet. Balance + status shown inline. Corporate employees
 *              are not listed: they have no wallet of their own, only their
 *              company's shared pool, which is managed on Company Wallets.
 * @author      Kinetika Labs
 * @created     2026-07-23
 * @changelog
 *   - 2026-07-23 Claude — Initial (Admin Wallet Management).
 *   - 2026-08-03 Claude — Retitled "Customer Wallets" — the wallet screens now
 *                          live in their own "Wallet Management" sidebar group.
 *   - 2026-08-04 Claude — Agent retirement (CR E8): "Agency Owner"/"Agency User"
 *                          role labels and the "Agency Owners/Users (B2B)"
 *                          filter options reworded to "Corporate Owner"/
 *                          "Corporate User".
 *   - 2026-08-10 Claude — User type filter now uses the themed Select
 *                          (console-wide dropdown consistency pass) instead of
 *                          a native <select>.
 *   - 2026-08-10 Claude — A B2B row's Balance/Wallet now read straight from
 *                          its company's pooled wallet (repo.searchUsers join
 *                          fix) and the Agency cell is labeled "(pooled)" — a
 *                          B2B user has no personal wallet, only the shared
 *                          one, and "Manage" on their row now redirects any
 *                          action to that pool (see WalletDetail.jsx).
 *   - 2026-09-12 Claude — Corporate employees are gone from this screen, and
 *                          with them the "User type" filter and the Agency
 *                          column. The 2026-08-10 pass made a B2B row REDIRECT
 *                          to the company pool; showing the row at all still
 *                          presented company money as one employee's wallet,
 *                          and a freeze from here quietly froze everyone. A
 *                          corporate account has exactly one wallet, the
 *                          company's — Wallet Management → Company Wallets is
 *                          now the only screen that touches it, and the server
 *                          refuses the per-user endpoints for these users.
 *   - 2026-09-25 Claude — `keepPreviousData: true` is a React Query **v4** option
 *                          and this workspace is on v5 (5.101), where no such
 *                          option exists and it was silently ignored. So every
 *                          page / filter / search change dropped `data` to
 *                          undefined, the table was swapped for six skeleton rows
 *                          and the panel collapsed ~700px before growing back —
 *                          the "pages flicker / the page moves" report. v5 spells
 *                          it `placeholderData: keepPreviousData` (a FUNCTION
 *                          imported from the package, not a boolean).
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import PageHead from '../../components/PageHead';
import Icon from '../../components/Icon';
import Chip from '../../components/Chip';
import Pager from '../../components/Pager';
import TableSkeleton from '../../components/TableSkeleton';
import { searchWalletUsers } from '../../api/wallets';
import { qk } from '../../api/queryKeys';

const inr = (n) => `₹${(Number(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const STATUS_TONE = { active: 'ok', frozen: 'warn', closed: 'rej' };

export default function WalletUsers() {
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [search, setSearch] = useState('');
  const [applied, setApplied] = useState('');

  const params = { page, pageSize };
  if (applied) params.q = applied;

  const { data, isLoading } = useQuery({
    queryKey: qk.wallet.users(params),
    queryFn: () => searchWalletUsers(params),
    placeholderData: keepPreviousData,
  });

  const rows = data?.data || [];
  const total = data?.meta?.total || 0;

  return (
    <>
      <PageHead
        title="Customer Wallets"
        subtitle="Wallet Management · retail customers — add money, view balances & history"
      />

      <article className="panel glass">
        <form className="search-row" onSubmit={(e) => { e.preventDefault(); setPage(1); setApplied(search); }}>
          <div className="field">
            <label>Name / Email / Mobile</label>
            <input placeholder="Search user…" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <button className="btn primary" type="submit"><Icon name="search" /> Search</button>
        </form>

        <p className="muted" style={{ margin: '0 0 12px' }}>
          Retail customers only. A corporate employee has no wallet of their own — their company shares one pooled wallet, managed on{' '}
          <Link to="/account/company-wallets">Company Wallets</Link>.
        </p>

        <div className="table-wrap">
          {isLoading ? <TableSkeleton /> : (
            <table className="table">
              <thead>
                <tr><th>#</th><th>Name</th><th>Email</th><th>Mobile</th><th>Balance</th><th>Wallet</th><th>Action</th></tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={7} className="empty">No customer wallets found.</td></tr>
                ) : rows.map((u, idx) => (
                  <tr key={u.id}>
                    <td className="mono">{(page - 1) * pageSize + idx + 1}</td>
                    <td><b>{`${u.firstName} ${u.lastName}`.trim() || '—'}</b></td>
                    <td>{u.email || <span className="muted">—</span>}</td>
                    <td>{u.mobile || <span className="muted">—</span>}</td>
                    <td className="mono"><b>{inr(u.balance)}</b></td>
                    <td><Chip tone={STATUS_TONE[u.walletStatus] || 'pend'}>{u.walletStatus}</Chip></td>
                    <td className="row-act">
                      <Link to={`/account/wallets/${u.id}`} className="btn ghost sm">Manage</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <Pager page={page} pageSize={pageSize} total={total} onPage={setPage} />
      </article>
    </>
  );
}
